import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Plus, Loader2, Save, Rocket, RotateCcw, Trash2, Eye, ArrowLeft,
    ArrowUp, ArrowDown, Globe, CheckCircle2, CircleDashed,
} from "lucide-react";
import { invalidateMenuPages } from "@/hooks/useMenuPages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCALE_LABELS = { fr: "Français", nl: "Nederlands", en: "English" };
const LOCALES = ["fr", "nl", "en"];

export default function PagesAdmin() {
    const [view, setView] = useState("list");
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newTitle, setNewTitle] = useState("");
    const [creating, setCreating] = useState(false);

    // edit state
    const [editId, setEditId] = useState(null);
    const [locale, setLocale] = useState("fr");
    const [meta, setMeta] = useState(null);
    const [values, setValues] = useState({});
    const [seoValues, setSeoValues] = useState({});
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/custom-pages`, { withCredentials: true });
            setPages(data);
        } catch {
            toast.error("Impossible de charger les pages.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (view === "list") loadList();
    }, [view, loadList]);

    const createPage = async () => {
        if (!newTitle.trim() || creating) return;
        setCreating(true);
        try {
            const { data } = await axios.post(`${API}/admin/custom-pages`, { title: newTitle.trim() }, { withCredentials: true });
            toast.success("Page créée. Complétez son contenu puis publiez.");
            setNewTitle("");
            invalidateMenuPages();
            openEdit(data.id);
        } catch {
            toast.error("Création impossible.");
        } finally {
            setCreating(false);
        }
    };

    const loadEdit = useCallback(async (id, loc) => {
        try {
            const { data } = await axios.get(`${API}/admin/custom-pages/${id}?locale=${loc}`, { withCredentials: true });
            setMeta(data);
            const base = {};
            data.fields.forEach((f) => { base[f.key] = ""; });
            setValues({ ...base, ...(data.content || {}), ...(data.draft || {}) });
            setSeoValues({ ...(data.seo || {}), ...(data.seo_draft || {}) });
        } catch {
            toast.error("Chargement de la page impossible.");
        }
    }, []);

    const openEdit = (id) => {
        setEditId(id);
        setLocale("fr");
        setMeta(null);
        setView("edit");
        loadEdit(id, "fr");
    };

    useEffect(() => {
        if (view === "edit" && editId) loadEdit(editId, locale);
    }, [locale]);

    const saveDraft = async () => {
        if (saving || !meta) return null;
        setSaving(true);
        try {
            const data = {};
            meta.fields.forEach((f) => { if (values[f.key] !== undefined) data[f.key] = values[f.key]; });
            const seo = {};
            meta.seo_schema.forEach((f) => { if (seoValues[f.key] !== undefined) seo[f.key] = seoValues[f.key]; });
            await axios.put(`${API}/admin/custom-pages/${editId}`, { locale, data, seo }, { withCredentials: true });
            toast.success("Brouillon enregistré.");
            return true;
        } catch {
            toast.error("Enregistrement impossible.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const publish = async () => {
        if (publishing) return;
        const ok = await saveDraft();
        if (ok === false) return;
        if (!window.confirm("Publier cette page sur le site public ?")) return;
        setPublishing(true);
        try {
            await axios.post(`${API}/admin/custom-pages/${editId}/publish`, {}, { withCredentials: true });
            toast.success("Page publiée.");
            invalidateMenuPages();
            await loadEdit(editId, locale);
        } catch (e) {
            const msg = e?.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Publication impossible.");
        } finally {
            setPublishing(false);
        }
    };

    const discard = async () => {
        if (!window.confirm("Abandonner les modifications non publiées ?")) return;
        try {
            await axios.post(`${API}/admin/custom-pages/${editId}/discard`, {}, { withCredentials: true });
            toast.success("Modifications abandonnées.");
            await loadEdit(editId, locale);
        } catch {
            toast.error("Opération impossible.");
        }
    };

    const openPreview = async () => {
        await saveDraft();
        const path = locale === "fr" ? `/${meta.slug}` : `/${locale}/${meta.slug}`;
        window.open(`${path}?preview=1`, "_blank", "noopener");
    };

    const removePage = async (id) => {
        if (!window.confirm("Supprimer définitivement cette page ?")) return;
        try {
            await axios.delete(`${API}/admin/custom-pages/${id}`, { withCredentials: true });
            toast.success("Page supprimée.");
            invalidateMenuPages();
            loadList();
        } catch {
            toast.error("Suppression impossible.");
        }
    };

    const toggleMenu = async (p) => {
        try {
            await axios.put(`${API}/admin/custom-pages/${p.id}/menu`, { menu_enabled: !p.menu_enabled }, { withCredentials: true });
            invalidateMenuPages();
            loadList();
        } catch {
            toast.error("Modification impossible.");
        }
    };

    const move = async (index, dir) => {
        const target = index + dir;
        if (target < 0 || target >= pages.length) return;
        const a = pages[index];
        const b = pages[target];
        try {
            await Promise.all([
                axios.put(`${API}/admin/custom-pages/${a.id}/menu`, { menu_order: b.menu_order }, { withCredentials: true }),
                axios.put(`${API}/admin/custom-pages/${b.id}/menu`, { menu_order: a.menu_order }, { withCredentials: true }),
            ]);
            invalidateMenuPages();
            loadList();
        } catch {
            toast.error("Réordonnancement impossible.");
        }
    };

    const renderField = (f, val, onChange, testPrefix) => {
        if (f.type === "textarea") {
            return <textarea data-testid={`${testPrefix}-${f.key}`} className="field" rows={6} value={val ?? ""} onChange={(e) => onChange(f.key, e.target.value)} />;
        }
        return (
            <>
                <input data-testid={`${testPrefix}-${f.key}`} type={f.type === "image" ? "url" : "text"} className="field" value={val ?? ""} onChange={(e) => onChange(f.key, e.target.value)} placeholder={f.type === "image" ? "https://…" : ""} />
                {f.type === "image" && val && <img src={val} alt={f.label} className="mt-3 max-h-32 border border-slate-200" />}
            </>
        );
    };

    // ---------- LIST VIEW ----------
    if (view === "list") {
        return (
            <section data-testid="pages-admin" className="space-y-8">
                <div>
                    <div className="overline text-[#64748B]">Gestion des pages</div>
                    <div className="font-serif text-2xl md:text-3xl">Pages du site</div>
                    <p className="text-sm text-[#64748B] mt-2 max-w-2xl">
                        Créez des pages (image d’en-tête, titre, texte, image), en 3 langues, avec SEO et gestion du menu.
                    </p>
                </div>

                <div className="border border-slate-200 bg-white p-6 flex flex-col sm:flex-row gap-3 sm:items-end">
                    <div className="flex-1">
                        <label className="overline text-[#64748B]">Titre de la nouvelle page (français)</label>
                        <input
                            data-testid="new-page-title"
                            className="field"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") createPage(); }}
                            placeholder="Ex. Nos cuisines sur mesure"
                        />
                    </div>
                    <button onClick={createPage} disabled={creating || !newTitle.trim()} data-testid="create-page-btn" className="btn-primary justify-center disabled:opacity-60">
                        {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} strokeWidth={1.5} />} Créer la page
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 text-[#64748B]"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
                ) : pages.length === 0 ? (
                    <div className="border border-dashed border-slate-300 p-16 text-center text-[#64748B]">Aucune page pour l’instant.</div>
                ) : (
                    <div className="space-y-3">
                        {pages.map((p, i) => (
                            <div key={p.id} data-testid={`page-row-${p.slug}`} className="border border-slate-200 bg-white p-5 flex flex-col md:flex-row md:items-center gap-4">
                                <div className="flex flex-col gap-1 md:w-1/2">
                                    <div className="font-serif text-xl">{p.title || p.slug}</div>
                                    <div className="text-xs text-[#64748B]">/{p.slug}</div>
                                    <div className="flex items-center gap-3 mt-1">
                                        {p.is_published ? (
                                            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700"><CheckCircle2 size={14} /> Publiée</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 text-xs text-[#DC2626]"><CircleDashed size={14} /> Brouillon</span>
                                        )}
                                        {p.has_draft && p.is_published && <span className="text-xs text-amber-600">Modifs non publiées</span>}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 md:ml-auto">
                                    <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`page-up-${p.slug}`} className="p-2.5 border border-[#1E3A5F]/20 hover:bg-slate-100 disabled:opacity-30" aria-label="Monter"><ArrowUp size={15} /></button>
                                    <button onClick={() => move(i, 1)} disabled={i === pages.length - 1} data-testid={`page-down-${p.slug}`} className="p-2.5 border border-[#1E3A5F]/20 hover:bg-slate-100 disabled:opacity-30" aria-label="Descendre"><ArrowDown size={15} /></button>
                                    <button
                                        onClick={() => toggleMenu(p)}
                                        data-testid={`page-menu-toggle-${p.slug}`}
                                        className={`text-xs px-3 py-2.5 border inline-flex items-center gap-2 transition-colors ${p.menu_enabled ? "border-[#1E3A5F] bg-[#1E3A5F] text-white" : "border-[#1E3A5F]/25 text-[#64748B]"}`}
                                        title="Afficher dans le menu"
                                    >
                                        <Globe size={14} /> {p.menu_enabled ? "Dans le menu" : "Hors menu"}
                                    </button>
                                    <button onClick={() => openEdit(p.id)} data-testid={`page-edit-${p.slug}`} className="text-sm px-4 py-2.5 border border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#F4F2EE]">Éditer</button>
                                    <button onClick={() => removePage(p.id)} data-testid={`page-delete-${p.slug}`} className="p-2.5 text-[#64748B] hover:text-[#DC2626] hover:bg-red-50" aria-label="Supprimer"><Trash2 size={16} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        );
    }

    // ---------- EDIT VIEW ----------
    if (!meta) {
        return <div className="flex items-center gap-2 text-[#64748B]"><Loader2 size={16} className="animate-spin" /> Chargement…</div>;
    }

    return (
        <section data-testid="page-editor" className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <button onClick={() => setView("list")} data-testid="page-back" className="text-sm text-[#64748B] hover:text-[#1E3A5F] inline-flex items-center gap-2 mb-2"><ArrowLeft size={15} /> Toutes les pages</button>
                    <div className="overline text-[#64748B]">Édition · /{meta.slug}</div>
                    <div className="font-serif text-2xl md:text-3xl">{meta.title || meta.slug} · {LOCALE_LABELS[locale]}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select value={locale} onChange={(e) => setLocale(e.target.value)} data-testid="page-locale-select" className="text-sm px-4 py-3 border border-[#1E3A5F]/25 bg-white">
                        {LOCALES.map((l) => <option key={l} value={l}>{LOCALE_LABELS[l]}</option>)}
                    </select>
                    <button onClick={openPreview} data-testid="page-preview" className="text-sm px-5 py-3 border border-[#1E3A5F]/25 hover:bg-[#1E3A5F] hover:text-white transition-colors inline-flex items-center gap-2"><Eye size={16} strokeWidth={1.5} /> Aperçu</button>
                    <button onClick={discard} data-testid="page-discard" className="text-sm px-5 py-3 border border-[#1E3A5F]/25 hover:bg-slate-100 transition-colors inline-flex items-center gap-2"><RotateCcw size={16} strokeWidth={1.5} /> Annuler</button>
                    <button onClick={saveDraft} disabled={saving} data-testid="page-save" className="text-sm px-5 py-3 bg-white border border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#F4F2EE] transition-colors inline-flex items-center gap-2">
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} strokeWidth={1.5} />} Enregistrer
                    </button>
                    <button onClick={publish} disabled={publishing} data-testid="page-publish" className="text-sm px-5 py-3 bg-[#1E3A5F] text-white hover:bg-[#152a45] transition-colors inline-flex items-center gap-2 disabled:opacity-60">
                        {publishing ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} strokeWidth={1.5} />} Publier
                    </button>
                </div>
            </div>

            {/* Content fields */}
            <div className="border border-slate-200 bg-white p-6 space-y-6">
                <div className="font-serif text-xl text-[#1E3A5F]">Contenu — {LOCALE_LABELS[locale]}</div>
                {meta.fields.map((f) => (
                    <div key={f.key}>
                        <label className="overline text-[#64748B]">{f.label}</label>
                        {renderField(f, values[f.key], (k, v) => setValues((s) => ({ ...s, [k]: v })), "page-field")}
                    </div>
                ))}
            </div>

            {/* SEO fields */}
            <div className="border border-slate-200 bg-white p-6 space-y-6">
                <div className="font-serif text-xl text-[#1E3A5F]">SEO — {LOCALE_LABELS[locale]}</div>
                {meta.seo_schema.map((f) => (
                    <div key={f.key}>
                        <label className="overline text-[#64748B]">{f.label}</label>
                        {renderField(f, seoValues[f.key], (k, v) => setSeoValues((s) => ({ ...s, [k]: v })), "page-seo")}
                    </div>
                ))}
            </div>
        </section>
    );
}
